import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCheck, FiArrowRight, FiGlobe, FiSend } = FiIcons;

export default function LeadMagnetModal({ isOpen, onClose }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    website: '',
    petType: '',
    goal: '',
    tone: ''
  });

  const handleNext = () => setStep(s => s + 1);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-brand-primaryDark/60 backdrop-blur-sm"
        />
        
        <motion.div 
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden"
        >
          <button 
            onClick={onClose}
            className="absolute right-6 top-6 text-gray-400 hover:text-brand-primaryDark transition-colors z-10"
          >
            <SafeIcon icon={FiX} size={24} />
          </button>

          <div className="p-8 md:p-10">
            {step < 5 && (
              <div className="mb-8">
                <span className="bg-brand-blush/20 text-brand-primaryDark text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                  Free Tool
                </span>
                <h3 className="text-2xl font-bold text-brand-primaryDark mt-4">Pet Content Planner</h3>
                <p className="text-gray-500 mt-2 text-sm">
                  Get a full week of content ideas in 60 seconds.
                </p>
              </div>
            )}

            <div className="space-y-6">
              {step === 1 && (
                <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}>
                  <label className="block text-sm font-bold text-brand-primaryDark mb-2">Your Website URL</label>
                  <div className="relative">
                    <SafeIcon icon={FiGlobe} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input 
                      type="url" 
                      placeholder="https://yourpetbusiness.com"
                      className="w-full pl-11 pr-4 py-4 rounded-xl border-2 border-gray-100 focus:border-brand-mediumPurple outline-none transition-all"
                      value={formData.website}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                    />
                  </div>
                  <button 
                    disabled={!formData.website}
                    onClick={handleNext}
                    className="w-full mt-6 bg-brand-primaryDark text-white py-4 rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    Next <SafeIcon icon={FiArrowRight} />
                  </button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}>
                  <label className="block text-sm font-bold text-brand-primaryDark mb-4">What's your main service?</label>
                  <div className="grid grid-cols-1 gap-3">
                    {['Dog Walking', 'Grooming', 'Training', 'Boarding/Daycare'].map(type => (
                      <button 
                        key={type}
                        onClick={() => { setFormData({...formData, petType: type}); handleNext(); }}
                        className="text-left p-4 rounded-xl border-2 border-gray-100 hover:border-brand-blush transition-all font-medium text-gray-700"
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}>
                  <label className="block text-sm font-bold text-brand-primaryDark mb-4">What's your current goal?</label>
                  <div className="grid grid-cols-1 gap-3">
                    {['Get more bookings', 'Build local trust', 'Save time on posts'].map(goal => (
                      <button 
                        key={goal}
                        onClick={() => { setFormData({...formData, goal: goal}); handleNext(); }}
                        className="text-left p-4 rounded-xl border-2 border-gray-100 hover:border-brand-blush transition-all font-medium text-gray-700"
                      >
                        {goal}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {step === 4 && (
                <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}>
                  <label className="block text-sm font-bold text-brand-primaryDark mb-2">Where should we send your plan?</label>
                  <input 
                    type="email" 
                    placeholder="your@email.com"
                    className="w-full px-4 py-4 rounded-xl border-2 border-gray-100 focus:border-brand-mediumPurple outline-none transition-all"
                  />
                  <button 
                    onClick={handleNext}
                    className="w-full mt-6 bg-brand-mediumPurple text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2"
                  >
                    Generate My Plan <SafeIcon icon={FiSend} />
                  </button>
                </motion.div>
              )}

              {step === 5 && (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6">
                  <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={FiCheck} size={40} />
                  </div>
                  <h4 className="text-2xl font-bold text-brand-primaryDark mb-3">Planner Ready!</h4>
                  <p className="text-gray-600 mb-8 leading-relaxed">
                    We're analyzing your website and goals. Your custom 7-day content calendar will hit your inbox in about 60 seconds.
                  </p>
                  <button 
                    onClick={onClose}
                    className="text-brand-mediumPurple font-bold hover:underline"
                  >
                    Back to Website
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}